import React, { useMemo } from "react";

const HotelWhatsAppShare = ({
    quotes,
    hotelData,
    selectedRoomIds = [],

    // You can override these if required
    checkIn,
    checkOut,
    numberOfRooms = 1,
    adults = 2,
    children = 0,

    contact = "8447455883",
    email = "sibo.sarso@gmail.com",

    buttonText = "Share on WhatsApp",
    className = "",
}) => {
    /**
     * Format date:
     * 2026-09-20 -> 20-Sep-26
     */
    const formatDate = (date) => {
        if (!date) return "";

        const d = new Date(date);

        const day = String(d.getDate()).padStart(2, "0");

        const month = d.toLocaleString("en-US", {
            month: "short",
        });

        const year = String(d.getFullYear()).slice(-2);

        return `${day}-${month}-${year}`;
    };


    /**
     * Format price:
     * 23414 -> 23,414/-
     */
    const formatPrice = (price) => {
        const amount = Number(price || 0);

        return amount.toLocaleString("en-IN", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    };


    /**
     * Get selected RoomInfo
     */
    const selectedRooms = useMemo(() => {

        if (!hotelData?.RoomInfo?.length) {
            return [];
        }

        return hotelData.RoomInfo.filter(room =>
            selectedRoomIds.includes(String(room.RoomId))
        );

    }, [hotelData, selectedRoomIds]);


    /**
     * Build WhatsApp message
     */
    const message = useMemo(() => {

        if (!hotelData?.HotelDetails) {
            return "";
        }

        const hotel = hotelData.HotelDetails;

        const hotelName = hotel.name || "";

        const city =
            hotel?.ad?.city?.name ||
            "";

        const rating = Number(hotel?.rt || 0);

        const ratingText =
            rating > 0
                ? ` | ${"⭐".repeat(rating)} ${rating} Star`
                : "";

        const finalCheckIn =
            checkIn ||
            hotelData?.searchQuery?.searchQuery?.checkinDate ||
            "";

        const finalCheckOut =
            checkOut ||
            hotelData?.searchQuery?.searchQuery?.checkoutDate ||
            "";

        let message = `🏨 *${hotelName}*
📍 *${city}*${ratingText}
━━━━━━━━━━━━━━━━━━━━
📅 *Check-in:* ${formatDate(finalCheckIn)}
📅 *Check-out:* ${formatDate(finalCheckOut)}
🛏️ *${numberOfRooms} Room${numberOfRooms > 1 ? "s" : ""}* | 👤 *${adults} Adult${adults > 1 ? "s" : ""}*`;

        if (children > 0) {
            message += ` | 👶 *${children} Child${children > 1 ? "ren" : ""}*`;
        }

        message += `
━━━━━━━━━━━━━━━━━━━━`;

        /**
         * Each selected RoomId becomes Option 1, Option 2, etc.
         */
        selectedRooms.forEach((room, index) => {

            /**
             * RoomInfo can contain multiple Rooms.
             *
             * For now we use the first rate/room.
             *
             * If later you want to select a particular
             * nested room/rate, we can also filter that.
             */
            const roomRate = room?.Rooms?.[0];

            if (!roomRate) {
                return;
            }

            const roomName =
                roomRate.RoomType ||
                roomRate.RoomCategory ||
                "Room";

            const mealPlan =
                roomRate.MealBase ||
                "Room Only";

            /**
             * Use nested room's HotelRoomPrice.
             *
             * Example:
             * 41576.07
             */
            const quoteValue = quotes[room.RoomId] ?? 0;
            console.log(quoteValue);

            const price =
                quoteValue !== undefined &&
                    quoteValue !== '' &&
                    !isNaN(parseFloat(quoteValue))
                    ? (parseFloat(quoteValue) + parseFloat(roomRate.HotelRoomPrice))
                    : roomRate.HotelRoomPrice;
            // const price =
            //     roomRate.HotelRoomPrice ??
            //     roomRate.TotalRoomPrice ??
            //     roomRate.TotalFare ??
            //     0;

            const formattedPrice = formatPrice(price);

            message += `

*Option ${index + 1}*
🛏️ ${roomName}
🍽️ ${mealPlan}
💰 *INR ${formattedPrice}/-*`;
        });

        return message;

    }, [
        quotes,
        hotelData,
        selectedRooms,
        checkIn,
        checkOut,
        numberOfRooms,
        adults,
        children,
    ]);


    /**
     * Open WhatsApp
     */
    const handleWhatsAppShare = () => {

        if (!selectedRooms.length) {
            return;
        }

        const encodedMessage = encodeURIComponent(message);

        const whatsappUrl =
            `https://api.whatsapp.com/send/?text=${encodedMessage}`;

        window.open(
            whatsappUrl,
            "_blank",
            "noopener,noreferrer"
        );
    };


    /**
     * Don't show button if no rooms selected
     */
    if (!selectedRooms.length) {
        return null;
    }


    return (
        <button
            type="button"
            onClick={handleWhatsAppShare}
            className={`
                flex w-auto items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50
                 ${className}
            `}
        >
            <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24" xmlns="http://w3.org">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.454 5.709 1.455h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg> WhatsApp
        </button>
    );
};

export default HotelWhatsAppShare;